import { PopoverFloating } from '@components/core-ui/PopoverFloating';
import EllipsisVerticalIcon from '@components/icons/EllipsisVertical.svg';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getNextMenuItemIndex } from './helpers/getNextMenuItemIndex';
import { TOverflowMenuItem } from './types/TOverflowMenuItem';
import { TProps } from './types/TProps';

const MenuItemContent = ({ item }: { item: TOverflowMenuItem }) => {
  const Icon = item.icon;

  return (
    <>
      {Icon ? <Icon className="h-4 w-4 shrink-0" /> : <span aria-hidden="true" className="h-4 w-4 shrink-0" />}
      {item.label}
    </>
  );
};

const getEnabledItems = (list: HTMLUListElement | null) =>
  Array.from(list?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);

export const OverflowMenu = ({ items }: TProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getEnabledItems(listRef.current)[0]?.focus();
    }
  }, [isOpen]);

  if (!items.length) {
    return null;
  }

  const close = () => setIsOpen(false);

  const closeAndRestoreFocus = () => {
    close();
    anchorRef.current?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndRestoreFocus();
      return;
    }
    if (event.key === 'Tab') {
      closeAndRestoreFocus();
      return;
    }
    const enabledItems = getEnabledItems(event.currentTarget);
    const currentIndex = enabledItems.findIndex((item) => item === document.activeElement);
    const nextIndex = getNextMenuItemIndex(event.key, currentIndex, enabledItems.length);
    if (nextIndex !== null) {
      event.preventDefault();
      enabledItems[nextIndex]?.focus();
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="group flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-[5px] text-foreground-muted hover:bg-tint hover:text-[var(--color-on-surface)]"
      >
        <EllipsisVerticalIcon className="h-6 w-6" />
      </button>
      <PopoverFloating open={isOpen} onClose={close} anchorEl={anchorRef.current} placement="bottom-end" offset={4}>
        <ul ref={listRef} role="menu" aria-label="More actions" className="menu" onKeyDown={onMenuKeyDown}>
          {items.map((item) => (
            <li key={item.key} role="none">
              {item.to ? (
                <Link role="menuitem" to={item.to} className="menu-item" onClick={close}>
                  <MenuItemContent item={item} />
                </Link>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onClick?.();
                  }}
                >
                  <MenuItemContent item={item} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </PopoverFloating>
    </>
  );
};
